import React from 'react';
import styled from 'styled-components';
import { useTheme } from '../../context/ThemeContext';
import { Check } from 'lucide-react';

interface StepIndicatorProps {
  currentStep: number;
  totalSteps: number;
  stepTitles: string[];
}

const Container = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 3rem;
  padding: 0 2rem;
  
  @media (max-width: 768px) {
    margin-bottom: 2rem;
    padding: 0 1rem;
  }
`;

const StepList = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0;
  max-width: 900px;
  width: 100%;
  position: relative;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 1rem;
  }
`;

const StepItem = styled.div.withConfig({
  shouldForwardProp: (prop) => !['theme', 'isLast'].includes(prop),
})<{ theme: any; isLast: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  position: relative;
  z-index: 2;
  
  ${props => !props.isLast && `
    &::after {
      content: '';
      position: absolute;
      top: 28px;
      left: 60%;
      right: -40%;
      height: 3px;
      background: linear-gradient(
        to right,
        ${props.theme.colors.border}40 0%,
        ${props.theme.colors.border}40 100%
      );
      z-index: 1;
      
      @media (max-width: 768px) {
        display: none;
      }
    }
  `}
  
  @media (max-width: 768px) {
    width: 100%;
    flex-direction: row;
    justify-content: flex-start;
    gap: 1rem;
    padding: 1rem;
    border-radius: 12px;
    background-color: ${props => props.theme.colors.surface}40;
    border: 1px solid ${props => props.theme.colors.border}30;
  }
`;

const StepCircle = styled.div.withConfig({
  shouldForwardProp: (prop) => !['theme', 'isActive', 'isCompleted'].includes(prop),
})<{ 
  theme: any; 
  isActive: boolean; 
  isCompleted: boolean; 
}>`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1rem;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  z-index: 3;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  
  ${props => {
    if (props.isCompleted) {
      return `
        background: linear-gradient(135deg, ${props.theme.colors.success} 0%, ${props.theme.colors.success}dd 100%);
        color: white;
        transform: scale(1.05);
        box-shadow: 0 6px 20px ${props.theme.colors.success}40;
      `;
    } else if (props.isActive) {
      return `
        background: linear-gradient(135deg, ${props.theme.colors.primary} 0%, #973031 100%);
        color: white;
        transform: scale(1.1);
        box-shadow: 0 8px 25px ${props.theme.colors.primary}50;
        animation: pulse 2s infinite;
      `;
    } else {
      return `
        background-color: ${props.theme.colors.surface};
        border: 3px solid ${props.theme.colors.border};
        color: ${props.theme.colors.textSecondary};
        transform: scale(0.95);
      `;
    }
  }}
  
  @keyframes pulse {
    0%, 100% {
      box-shadow: 0 8px 25px ${props => props.theme.colors.primary}50;
    }
    50% {
      box-shadow: 0 8px 25px ${props => props.theme.colors.primary}70;
    }
  }
  
  @media (max-width: 768px) {
    width: 48px;
    height: 48px;
    font-size: 0.875rem;
    flex-shrink: 0;
  }
`;

const StepContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 1rem;
  text-align: center;
  
  @media (max-width: 768px) {
    align-items: flex-start;
    text-align: left;
    margin-top: 0;
    flex: 1;
  }
`;

const StepTitle = styled.h4.withConfig({
  shouldForwardProp: (prop) => !['theme', 'isActive', 'isCompleted'].includes(prop),
})<{ 
  theme: any; 
  isActive: boolean; 
  isCompleted: boolean; 
}>`
  font-weight: ${props => (props.isActive || props.isCompleted) ? '700' : '500'};
  color: ${props => {
    if (props.isCompleted) return props.theme.colors.success;
    if (props.isActive) return props.theme.colors.primary;
    return props.theme.colors.textSecondary;
  }};
  font-size: 0.9rem;
  margin: 0 0 0.25rem 0;
  transition: all 0.3s ease;
  max-width: 120px;
  line-height: 1.3;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    max-width: none;
    margin-bottom: 0.5rem;
  }
`;


const ProgressBar = styled.div.withConfig({
  shouldForwardProp: (prop) => !['theme', 'progress'].includes(prop),
})<{ theme: any; progress: number }>`
  position: absolute;
  top: 28px;
  left: 0;
  right: 0;
  height: 3px;
  background-color: ${props => props.theme.colors.border}30;
  border-radius: 2px;
  z-index: 1;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: ${props => props.progress}%;
    background: linear-gradient(90deg, ${props => props.theme.colors.success} 0%, ${props => props.theme.colors.primary} 100%);
    border-radius: 2px;
    transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const StepIndicator: React.FC<StepIndicatorProps> = ({
  currentStep,
  totalSteps,
  stepTitles,
}) => {
  const { theme } = useTheme();


  const calculateProgress = () => {
    return ((currentStep - 1) / (totalSteps - 1)) * 100;
  };

  return (
    <Container>
      <StepList>
        <ProgressBar theme={theme} progress={calculateProgress()} />
        {Array.from({ length: totalSteps }, (_, index) => {
          const stepNumber = index + 1;
          const isActive = stepNumber === currentStep;
          const isCompleted = stepNumber < currentStep;
          const isLast = stepNumber === totalSteps;

          return (
            <StepItem
              key={stepNumber}
              theme={theme}
              isLast={isLast}
            >
              <StepCircle
                theme={theme}
                isActive={isActive}
                isCompleted={isCompleted}
              >
                {isCompleted ? <Check size={20} /> : stepNumber}
              </StepCircle>
               <StepContent>
                 <StepTitle
                   theme={theme}
                   isActive={isActive}
                   isCompleted={isCompleted}
                 >
                   {stepTitles[index]}
                 </StepTitle>
               </StepContent>
            </StepItem>
          );
        })}
      </StepList>
    </Container>
  );
};

export default StepIndicator;
